/**
 * @file EditConversation.jsx
 * @description EditConversation component for editing text conversations.
 * @component EditConversation
 * @exports EditConversation - Main component for editing text conversations.
 */

import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Button,
  Container,
  Typography,
  Chip,
  Box,
  IconButton,
  Tabs,
  Tab,
  Alert,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useTheme } from "@mui/material/styles";
import ArrowCircleLeftOutlinedIcon from "@mui/icons-material/ArrowCircleLeftOutlined";
import LocalPhoneIcon from "@mui/icons-material/LocalPhone";
import EmailIcon from "@mui/icons-material/Email";
import CampaignIcon from "@mui/icons-material/Campaign";

import {
  ALIGN,
  BUTTON_COLORS,
  DISPLAY,
  SIZES,
  STATUS,
  TYPOGRAPHY_VARIANT,
  VARIANTS,
} from "../../../common/constants/commonConstants.js";
import { phoneNumber } from "../../../utils/utils.js";
import {
  TEXT_CONVERSATION_TABS,
  TEXT_ERRORS,
  TEXT_MESSAGE,
  TEXT_SUCCUSS,
  TEXT_STRING,
} from "../../../common/constants/agentModule/textConstants.js";
import {
  assignUsers,
  endTextConversation,
  getTextConversationById,
  updateCaraResponseChat,
} from "../../../services/agentModule/text/text-conversation.js";
import CardComponent from "../../../common/components/CardComponent.jsx";
import ProgressOverlay from "../../../common/components/ProgressOverlay.jsx";
import EndConversationDailog from "../common/EndConversationDailog.jsx";
import { getUsers } from "../../../services/adminModule/users/userServices.js";
import AssignModal from "../common/AssignModal.jsx";
import EditCaraResponseModal from "../common/EditCaraResponseModal.jsx";

const EditConversation = () => {
  const { customerdataID } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [openAssignModal, setOpenAssignModal] = useState(false);
  const [openEndConversationDailog, setOpenEndConversationDailog] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [selectedChat, setSelectedChat] = useState(null);
  const [openEditCaraModal, setopenEditCaraModal] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const theme = useTheme();

  useEffect(() => {
    const fetchTextConversations = async () => {
      try {
        setIsLoading(true);
        const fetchedChatRecords = await getTextConversationById(customerdataID);
        if (fetchedChatRecords && fetchedChatRecords.length > 0) {
          setCustomer(fetchedChatRecords[0]);
        }
      } catch (e) {
        console.error(TEXT_ERRORS.FETCH_TEXT_CONVERSATION_BY_ID_ERROR, e);
      } finally {
        setIsLoading(false);
      }
    };
    const fetchUserDetails = async () => {
      try {
        const users = await getUsers();
        if (users) setUsers(users);
      } catch (error) {
        console.error(TEXT_ERRORS.FETCH_USER_INFO_ERROR, error);
      }
    };
    fetchUserDetails();
    fetchTextConversations();
  }, [customerdataID]);

  const handleEndConversationClick = () => {
    setOpenEndConversationDailog(true);
  };

  const handleEditClick = (chat) => {
    setSelectedChat({ ...chat, id: chat.id });
    setopenEditCaraModal(true);
  };

  const handleModalClose = () => {
    setOpenEndConversationDailog(false);
    setSelectedChat(null);
  };

  const handleConfirmEndConversation = async () => {
    try {
      setOpenEndConversationDailog(false);
      setIsLoading(true);
      await endTextConversation(customerdataID);
      setSuccessMessage(TEXT_SUCCUSS.END_CONVERSATION);
      setTimeout(() => setSuccessMessage(""), 3000);
      navigate(-1);
    } catch (error) {
      console.error(TEXT_ERRORS.END_CONVERSATION_ERROR, error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditCaraResponseSave = async () => {
    if (selectedChat) {
      const updatedData = {
        id: selectedChat.id,
        cararesponse: selectedChat.cararesponse,
        cararesponseapproved: selectedChat.cararesponseapproved,
        userguid: localStorage.getItem("userguid")
      };
      try {
        setopenEditCaraModal(false);
        setIsLoading(true);
        await updateCaraResponseChat(updatedData);
        const updatedChatData = customer.chatData.map((item) =>
          item.id === updatedData.id ? { ...item, cararesponse: updatedData.cararesponse } : item
        );
        setCustomer({ ...customer, chatData: updatedChatData });
      } catch (error) {
        console.error(TEXT_ERRORS.UPDATE_CARA_RESPONSE_ERROR, error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleUpdateAssignee = async (id, newAssignee) => {
    try {
      setIsLoading(true);
      await assignUsers(id, newAssignee);
      setOpenAssignModal(false);
      setSuccessMessage(TEXT_SUCCUSS.ASSIGNEE_UPDATE);
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      console.error(TEXT_ERRORS.UPDATE_ASSIGNEE_ERROR, error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container maxWidth={false} disableGutters>
      {successMessage && <Alert severity={STATUS.SUCCESS} sx={{ mb: 2 }}>{successMessage}</Alert>}
      {isLoading && <ProgressOverlay />}

      <Grid container spacing={2} sx={{ mb: 2, alignItems: ALIGN.CENTER }}>
        <Grid size={SIZES.AUTO}>
          <IconButton color={BUTTON_COLORS.SECONDARY} onClick={() => navigate(-1)}>
            <ArrowCircleLeftOutlinedIcon />
          </IconButton>
        </Grid>
        {customer && (
          <>
            <Grid size={SIZES.AUTO}>
              <Typography variant={TYPOGRAPHY_VARIANT.H6}>
                {customer.firstname} {customer.lastname}
              </Typography>
            </Grid>
            <Grid size={SIZES.GROW}>
              <Chip icon={<LocalPhoneIcon />} label={phoneNumber(customer.phonenumber)} size={SIZES.SMALL} sx={{ mx: 1 }} />
              <Chip icon={<EmailIcon />} label={customer.emailaddress} size={SIZES.SMALL} />
              <Chip icon={<CampaignIcon />} label={customer.campaignname} size={SIZES.SMALL} sx={{ mx: 1 }} />
            </Grid>
            <Box sx={{ ml: "auto", display: DISPLAY.FLEX, gap: 2 }}>
              {customer.convend === 0 && (
                <>
                  <Button variant={VARIANTS.OUTLINED} color={BUTTON_COLORS.PRIMARY} size={SIZES.SMALL} onClick={() => setOpenAssignModal(true)}>
                    {TEXT_STRING.ASSIGN}
                  </Button>
                  <Button variant={VARIANTS.OUTLINED} color={BUTTON_COLORS.PRIMARY} size={SIZES.SMALL} onClick={handleEndConversationClick}>
                    {TEXT_STRING.END_CONVERSATION}
                  </Button>
                </>
              )}
            </Box>
          </>
        )}
      </Grid>

      <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} sx={{ mb: 2 }}>
        <Tab label={TEXT_CONVERSATION_TABS.CONVERSATION} />
        <Tab label={TEXT_CONVERSATION_TABS.PROFILE} />
        <Tab label={TEXT_CONVERSATION_TABS.NOTES} />
        <Tab label={TEXT_CONVERSATION_TABS.PAYMENT} />
        <Tab label={TEXT_CONVERSATION_TABS.HISTORY} />
        <Tab label={TEXT_CONVERSATION_TABS.CHATS} />
        <Tab label={TEXT_CONVERSATION_TABS.CALLS} />
        <Tab label={TEXT_CONVERSATION_TABS.EMAILS} />
      </Tabs>

      <Box sx={{ display: "flex", height: "70vh" }}>
        {/* Transcript/Conversation always shown */}
        <Box sx={{ width: tabValue === 0 ? "100%" : "50%", pr: 2, overflowY: "auto" }}>
          {customer ? (
            customer.chatData?.length ? (
              <CardComponent hasContentBg={true}>
                {customer.chatData.map((chat, index) => (
                  <Box key={index} className="ai-chatbox">
                    <Box className="ai-chatbox__message ai-chatbox__message--input" sx={{ borderLeftColor: theme.palette.primary.main + "60" }}>
                      <Box className="ai-chatbox__message-content">{chat.userresponse}</Box>
                      <Box className="ai-chatbox__message-footer">
                        <span><b>User</b> on {new Date(chat.userresponsetime).toLocaleString()}</span>
                      </Box>
                    </Box>
                    <Box className="ai-chatbox__message ai-chatbox__message--response" sx={{ borderColor: chat.cararesponseapproved === 0 ? theme.palette.warning.main : theme.palette.primary.main + "30" }}>
                      <Box className="ai-chatbox__message-content">{chat.cararesponse}</Box>
                      <Box className="ai-chatbox__message-footer">
                        <span><b>Cara</b> on {new Date(chat.cararesponsetime).toLocaleString()}</span>
                        {chat.cararesponseapproved === 0 && (
                          <Button variant={VARIANTS.TEXT} onClick={() => handleEditClick(chat)} sx={{ ml: 2 }}>{TEXT_STRING.EDIT_RESPONSE}</Button>
                        )}
                      </Box>
                    </Box>
                  </Box>
                ))}
              </CardComponent>
            ) : (
              <Typography>{TEXT_MESSAGE.NO_TEXT_CONVERSATION}</Typography>
            )
          ) : (
            <Typography>Loading...</Typography>
          )}
        </Box>

        {/* Right Side Panel for all other tabs */}
        {tabValue !== 0 && (
          <Box sx={{ width: "50%", pl: 2, overflowY: "auto" }}>
            {tabValue === 1 && <Typography>Profile placeholder</Typography>}
            {tabValue === 2 && <Typography>Notes placeholder</Typography>}
            {tabValue === 3 && <Typography>Payment placeholder</Typography>}
            {tabValue === 4 && <Typography>History placeholder</Typography>}
            {tabValue === 5 && <Typography>Chats placeholder</Typography>}
            {tabValue === 6 && <Typography>Calls placeholder</Typography>}
            {tabValue === 7 && <Typography>Emails placeholder</Typography>}
          </Box>
        )}
      </Box>

      {openEditCaraModal && (
        <EditCaraResponseModal
          open={openEditCaraModal}
          selectedChat={selectedChat}
          onClose={handleModalClose}
          onSave={handleEditCaraResponseSave}
          onTextChange={(text) => setSelectedChat({ ...selectedChat, cararesponse: text })}
          onCheckBoxChange={(checked) => setSelectedChat({ ...selectedChat, cararesponseapproved: checked })}
        />
      )}

      {openAssignModal && (
        <AssignModal
          open={openAssignModal}
          customerId={customerdataID}
          users={users}
          assignedCust={customer.assignedto}
          onAssign={handleUpdateAssignee}
          onClose={() => setOpenAssignModal(false)}
        />
      )}

      <EndConversationDailog
        open={openEndConversationDailog}
        onClose={() => setOpenEndConversationDailog(false)}
        onConfirm={handleConfirmEndConversation}
      />
    </Container>
  );
};

export default EditConversation;